import { ProgressBar } from "primereact/progressbar";
import { Tooltip } from "primereact/tooltip";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";

import ButtonZegel from "../../components/Buttons/ButtonZegel";
import DialogZegel from "../../components/Dialog/DialogZegel";
import FooterInterno from "../../components/Footer/FooterInterno";
import useAlumno from "../../hooks/useAlumno";
import { duracionEnHoras, timeToSeconds } from "../../helpers/helpers";
import { PrivateRoutes } from "../../routes/routes";

import { BuscarCursoID, ObtenerProgresoCursoPorPersonaCurso } from "../../services/CursoService";
import { ObtenerEvaluacionesPersonaCurso } from "../../services/EvaluacionService";
import { ListarBibliotecasPorCurso } from "../../services/BibliotecaServices";
import { ListarLeccionesPorUnidad } from "../../services/LeccionServices";
import { ListarUnidadesPorCurso } from "../../services/UnidadService";
import {
  ActualizarCursoEstado,
  ObtenerCursosPorUsuarioCurso,
  ObtenerPersonaPorId,
} from "../../services/UsuarioService";

import BibliotecaVirtualComponent from "../components/BibliotecaVirtual/BibliotecaVirtualComponent";
import CardBienvenida from "../components/CardBienvenida/CardBienvenida";
import GroupCardDownload from "../components/CardDownload/GroupCardDownload";
import LoadingGlobal from "../components/LoadingGlobal/LoadingGlobal";
import MiProgresoTituloComponent from "../components/MiProgresoTitulo/MiProgresoTituloComponent";
import PreguntasFrecuentesComponent from "../components/PreguntasFrecuentes/PreguntasFrecuentesComponent";
import TabUnidadesComponent from "../components/TabUnidades/TabUnidadesComponent";

import "./styles/ProgresoCurso.scss";

const INITIAL_CURSO = { nombre: "" };
const INITIAL_UNIDADES = [{ idUnidad: 0 }];
const INITIAL_CURSO_PERSONA = [{ idEstado: 2 }];
const INITIAL_PROGRESO = [{ idUnidad: 0, promedioPorcentaje: 0 }];

const calcularPorcentajeVideo = (tiempoActual, tiempoTotal) => {
  const fechaActual = new Date(tiempoActual);
  const fechaTotal = new Date(tiempoTotal);
  
  const opcionesHora = { hour: "2-digit", minute: "2-digit", second: "2-digit" };
  const horaActual = fechaActual.toLocaleTimeString("es-ES", opcionesHora);
  const horaTotal = fechaTotal.toLocaleTimeString("es-ES", opcionesHora);
  
  const segundosActual = timeToSeconds(horaActual) + 15;
  const segundosTotal = timeToSeconds(horaTotal);
  
  const porcentaje = Math.round((segundosActual * 100) / segundosTotal);
  return Math.min(porcentaje, 100);
};

const calcularPromedioUnidad = (progresosUnidad, cantidadLecciones) => {
  if (!progresosUnidad.length || !cantidadLecciones) return 0;
  
  const totalPorcentaje = progresosUnidad.reduce(
    (sum, item) => sum + item.PorcentajeAvance, 
    0
  );
  return Math.round(totalPorcentaje / cantidadLecciones);
};

const obtenerNotaMaximaPorUnidad = (evaluaciones) => {
  const notasMaximas = new Map();
  
  evaluaciones.forEach(({ idUnidad, nota }) => {
    const notaActual = notasMaximas.get(idUnidad) || 0;
    if (nota > notaActual) {
      notasMaximas.set(idUnidad, nota);
    }
  });
  
  return notasMaximas;
};

const validarCursoExpirado = (cursoPersona) => {
  if (!cursoPersona?.length) return { expirado: false, ampliacion: false, membresia: false };
  
  const curso = cursoPersona[0];
  const ahora = new Date();
  
  let expirado = false;
  let ampliacion = false;
  let membresia = false;
  
  if (new Date(curso.fechaVigencia) < ahora) {
    expirado = true;
    if (curso.fechaAmpliacionCurso && new Date(curso.fechaAmpliacionCurso) < ahora) {
      ampliacion = true;
    }
  }
  
  if (curso.fechaVigenciaMembresia && new Date(curso.fechaVigenciaMembresia) < ahora) {
    membresia = true;
    expirado = true;
  }
  
  return { expirado, ampliacion, membresia };
};

const ProgresoCurso = () => {
  const navigate = useNavigate();
  const { perfil, configuraciones } = useAlumno();
  const { idCurso } = useParams();
  
  const isMounted = useRef(false);
  const datosYaCargados = useRef(false);
  const cursoCargado = useRef(false);
  const bibliotecaCargada = useRef(false);

  const [curso, setCurso] = useState(INITIAL_CURSO);
  const [listaUnidades, setListaUnidades] = useState(INITIAL_UNIDADES);
  const [listaLecciones, setListaLecciones] = useState(null);
  const [listaBiblioteca, setListaBiblioteca] = useState([]);
  const [cursoPersona, setCursoPersona] = useState(INITIAL_CURSO_PERSONA);
  const [persona, setPersona] = useState([]);
  
  const [progresoUnidad, setProgresoUnidad] = useState(INITIAL_PROGRESO);
  const [progresoCursoData, setProgresoCursoData] = useState(null);
  const [leccionesPendientes, setLeccionesPendientes] = useState(null);
  const [porcentajeCursoAvance, setPorcentajeCursoAvance] = useState(0);
  
  const [evaluaciones, setEvaluaciones] = useState(null);
  const [evaluacionesProgreso, setEvaluacionesProgreso] = useState(null);
  
  const [loadingGlobal, setLoadingGlobal] = useState(false);
  const [spinnerMateriales, setSpinnerMateriales] = useState(false);
  const [spinnerCertificado, setSpinnerCertificado] = useState(false);
  const [spinnerNotas, setSpinnerNotas] = useState(false);
  const [isButtonDisabledMateriales, setButtonDisabledMateriales] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [contenidoAlertActivar, setContenidoAlertActivar] = useState(null);
  
  const estadoExpiracion = useMemo(() => validarCursoExpirado(cursoPersona), [cursoPersona]);

  useEffect(() => {
    if (!idCurso || cursoCargado.current) return;
    cursoCargado.current = true;
    const jwt = localStorage.getItem("jwt");
    BuscarCursoID({ jwt, idCurso }).then((data) => {

      if (!data) navigate(`/${PrivateRoutes.HOME}/Dashboard`);
      else setCurso(data);

    });
  }, [idCurso, navigate]);


  useEffect(() => {
    if (!idCurso || bibliotecaCargada.current) return;
    bibliotecaCargada.current = true;
    const jwt = localStorage.getItem("jwt");
    ListarBibliotecasPorCurso({ jwt, idCurso }).then(setListaBiblioteca);
  }, [idCurso]);

  useEffect(() => {
    if (!perfil?.idUsuario || !idCurso || datosYaCargados.current) return;

    const cargarTodosDatos = async () => {
      setLoadingGlobal(true);
      datosYaCargados.current = true;
      
      try {
        const jwt = localStorage.getItem("jwt");
        const idUsuario = perfil.idUsuario.toString();
        
        // 1. Obtener curso persona
        const dataCursoPersona = await ObtenerCursosPorUsuarioCurso({ jwt, idUsuario, idCurso });        
        if (!dataCursoPersona?.length) {
          navigate(`/${PrivateRoutes.HOME}/Dashboard`);
          return;
        }
        
        setCursoPersona(dataCursoPersona);
        const idPersonaCurso = dataCursoPersona[0].idPersonaCurso;
        
        // 2. Cargar datos en paralelo (Promise.all para optimizar)
        const [dataPersona, dataProgreso, dataUnidades, dataEvaluaciones] = await Promise.all([
          ObtenerPersonaPorId({ jwt, idPersona: idUsuario }),
          ObtenerProgresoCursoPorPersonaCurso({ jwt, idPersonaCurso }),
          ListarUnidadesPorCurso({ jwt, idCurso }),
          ObtenerEvaluacionesPersonaCurso({ jwt, id: idPersonaCurso })
        ]);
        
        setPersona(dataPersona);
        setProgresoCursoData(dataProgreso);
        setListaUnidades(dataUnidades);
        
        // 3. Procesar evaluaciones
        if (Array.isArray(dataEvaluaciones) && dataEvaluaciones.length > 0) {
          const primerIntento = dataEvaluaciones.filter(e => e.intento === 1);
          const progreso = primerIntento.length > 0
            ? (primerIntento.length / dataUnidades.length) * 100
            : 0.001;
          
          setEvaluacionesProgreso(progreso);
          setEvaluaciones(dataEvaluaciones);
        } else {
          setEvaluacionesProgreso(0.001);
          setEvaluaciones([]);
        }
        
        // 4. Cargar lecciones de todas las unidades EN PARALELO
        const promesasLecciones = dataUnidades.map(unidad => 
          ListarLeccionesPorUnidad({ jwt, idUnidad: unidad.idUnidad })
        );
        
        const resultadosLecciones = await Promise.all(promesasLecciones);
        
        // 5. Procesar progreso de todas las unidades
        const todasLecciones = [];
        const progresoPorUnidad = [];
        const leccionesPendientesPorUnidad = [];
        
        resultadosLecciones.forEach((dataLecciones, indexUnidad) => {
          const unidad = dataUnidades[indexUnidad];
          const { idUnidad } = unidad;
          
          const progresoUnidadActual = [];
          const leccionesPendientesUnidad = { idUnidad, leccionesPendientes: [] };
          
          dataLecciones.forEach((leccion, indexLeccion) => {
            const progresoLeccion = dataProgreso.find(p => p.idLeccion === leccion.idLeccion);
            
            if (progresoLeccion) {
              const porcentaje = calcularPorcentajeVideo(
                progresoLeccion.tiempoVideoActual,
                progresoLeccion.tiempoVideoTotal
              );
              
              progresoUnidadActual.push({
                idUnidad,
                ...progresoLeccion,
                PorcentajeAvance: porcentaje,
                cantidadLecciones: dataLecciones.length,
              });
              
              if (porcentaje < 100) {
                leccionesPendientesUnidad.leccionesPendientes.push(indexLeccion + 1);
              }
            } else {
              leccionesPendientesUnidad.leccionesPendientes.push(indexLeccion + 1);
            }
          });
          
          const promedio = calcularPromedioUnidad(progresoUnidadActual, dataLecciones.length);
          progresoPorUnidad.push({ idUnidad, promedioPorcentaje: promedio });
          
          todasLecciones.push(...dataLecciones);
          leccionesPendientesPorUnidad.push(leccionesPendientesUnidad);
        });
        
        setListaLecciones(todasLecciones);
        setProgresoUnidad(progresoPorUnidad);
        setLeccionesPendientes(leccionesPendientesPorUnidad);
        
      } catch (error) {
        console.error("Error cargando datos del curso:", error);
        datosYaCargados.current = false; // Permitir reintentar en caso de error
      } finally {
        setLoadingGlobal(false);
        isMounted.current = true;
      }
    };
    
    cargarTodosDatos();
  }, [perfil, idCurso, navigate]);

  // ===== CALCULAR PORCENTAJE DE AVANCE TOTAL (Solo cuando cambian los datos) =====
  useEffect(() => {
    if (!listaLecciones?.length || !progresoCursoData || evaluacionesProgreso === null) return;    
    const porcentajesPorLeccion = new Map();
    
    progresoCursoData.forEach((tiempo) => {
      const tiempoActual = moment(tiempo.tiempoVideoActual).format("HH:mm:ss");
      const tiempoTotal = moment(tiempo.tiempoVideoTotal).format("HH:mm:ss");
      const porcentaje = (timeToSeconds(tiempoActual) / timeToSeconds(tiempoTotal)) * 100;
      
      const porcentajeActual = porcentajesPorLeccion.get(tiempo.idLeccion) || 0;
      if (porcentaje > porcentajeActual) {
        porcentajesPorLeccion.set(tiempo.idLeccion, porcentaje);
      }
    });
    
    const porcentajes = Array.from(porcentajesPorLeccion.values());
    const promedio = porcentajes.reduce((sum, p) => sum + p, 0) / listaLecciones.length;
    const promedioFinal = (isNaN(promedio) ? 0 : promedio) * 0.5 + evaluacionesProgreso * 0.5;
    
    setPorcentajeCursoAvance(Math.round(Math.min(promedioFinal, 100)));
  }, [listaLecciones, progresoCursoData, evaluacionesProgreso]);

  const unidadesProbadasNotaMinima = useCallback((evals, configs) => {
    if (!evals || !configs) return false;
    
    const notasMaximas = obtenerNotaMaximaPorUnidad(evals);
    const notaMinima = parseFloat(
      configs.find(z => z.codigo === "NOTA_MINIMA_APROB")?.valor || 0
    );
    
    return !Array.from(notasMaximas.values()).some(nota => nota < notaMinima);
  }, []);

  // ===== ACTUALIZAR ESTADO DEL CURSO =====
  const actualizarEstadoCurso = useCallback(() => {
    if (!cursoPersona?.[0]) return;
    
    const { idPersonaCurso, idCurso: cursoId, finCurso, idUsuario } = cursoPersona[0];
    const jsonCurso = JSON.stringify({
      idPersonaCurso,
      idUsuario,
      idCurso: cursoId,
      finCurso,
      idEstado: 2,
    });
    
    const jwt = localStorage.getItem("jwt");
    
    ActualizarCursoEstado({ jsonCurso, jwt }).then(() => {
      setTimeout(() => {
        setShowAlert(false);
        window.location.reload();
      }, 3000);
    });
  }, [cursoPersona]);

  // ===== COMPONENTE: EVALUACIÓN DE UNIDAD (Memoizado) =====
  const UnidadEvaluacion = useCallback(({ idUnidad }) => {
    if (!evaluaciones) {
      return <span className="fontsizesmall">Evaluación: --</span>;
    }
    
    const evaluacionesUnidad = evaluaciones.filter(e => e.idUnidad === idUnidad);
    const progresoUnidadActual = progresoUnidad.find(p => p.idUnidad === idUnidad);
    const notaMinima = parseFloat(
      configuraciones?.find(z => z.codigo === "NOTA_MINIMA_APROB")?.valor || 0
    );
    
    if (evaluacionesUnidad.length > 0) {
      const notas = evaluacionesUnidad.map(e => e.nota);
      const notaMaxima = Math.max(...notas);
      const cantidadIntentos = notas.length;
      const esAprobado = notaMaxima >= notaMinima;
      
      const notaFormateada = notaMaxima <= 9 ? `0${notaMaxima}` : notaMaxima;
      const claseSpan = (cantidadIntentos === 2 || esAprobado)
        ? "fontsizesmall"
        : "fontsizesmallUnidad";
      const colorNota = esAprobado ? "black" : "red";
      
      return (
        <>
          <span className={claseSpan}>
            Evaluación: <span style={{ color: colorNota }}>{notaFormateada}</span>
          </span>
          {!esAprobado && cantidadIntentos < 2 && (
            <Tooltip
              target=".fontsizesmallUnidad"
              content="¡Recuerda dar tu evaluación! Te queda un intento"
              className="white-tooltip"
              position="left"
            />
          )}
        </>
      );
    }
    
    const mostrarTooltip = progresoUnidadActual && (progresoUnidadActual.promedioPorcentaje > 0);
    const claseSpan = mostrarTooltip ? "fontsizesmallUnidad targetTooltip" : "fontsizesmall targetTooltip";
    
    return (
      <>
        <span className={claseSpan}>Evaluación: --</span>
        {mostrarTooltip && (
          <Tooltip
            target=".targetTooltip"
            content="¡Recuerda dar tu evaluación! Revisa que hayas completado al menos un intento con nota aprobatoria"
            className="white-tooltip"
            position="left"
          />
        )}
      </>
    );
  }, [evaluaciones, progresoUnidad, configuraciones]);

  // ===== COMPONENTE: VISTA PROMEDIO (Memoizado) =====
  const VistaPromedio = useCallback(({ promedio }) => {
    const esNumerico = !isNaN(promedio);
    const promedioNum = parseFloat(promedio);
    const promedioAprobado = 13;
    const color = !esNumerico ? "black" : promedioNum < promedioAprobado ? "red" : "black";
    
    const mensaje = promedioNum < promedioAprobado
      ? "Recuerda que para obtener tu certificado debes contar con al menos 13 puntos en el promedio"
      : "¡Felicitaciones! Obtuviste una nota aprobatoria.";
    
    return (
      <>
        {esNumerico && (
          <Tooltip
            target=".promedio-tooltip"
            position="top"
            className="white-tooltip"
            content={mensaje}
          />
        )}
        <span className="subtitle h4 promedio-tooltip">
          Promedio total del curso:
          <span style={{ color, fontWeight: "bold" }}> {promedio}</span>
        </span>
      </>
    );
  }, []);

  // ===== RENDERIZADO CONDICIONAL =====
  if (!perfil) return null;
  
  if (!cursoPersona?.length || !cursoPersona[0]?.idPersonaCurso) {
    return <LoadingGlobal loadingGlobal={loadingGlobal} />;
  }

  // ===== FOOTER DEL DIÝLOGO =====
  const footerActivar = (
    <>
      <div style={{ marginBottom: 8 }}>
        <ButtonZegel
          label="Activar curso"
          color="primary"
          onClick={actualizarEstadoCurso}
        />
      </div>
      <div>
        <ButtonZegel
          label="Cancelar"
          color="secondary"
          onClick={() => setShowAlert(false)}
        />
      </div>
    </>
  );

  // ===== RENDERIZADO PRINCIPAL =====
  return (
    <>
      <LoadingGlobal loadingGlobal={loadingGlobal} />
      
      <div className="dashboard-progreso">
        {/* Header */}
        <div className="progreso-header">
          <div className="container-center-auto">
            <button
              className="p-link-button button-img"
              onClick={() => navigate(`/${PrivateRoutes.HOME}/Dashboard`)}
              style={{ fontWeight: "bold", color: "#000" }}
            >
              <i className="pi pi-arrow-left font-bold" style={{ fontSize: "1rem" }} />
            </button>
          </div>
        </div>

        {/* Contenido */}
        <div className="progreso-contenido content-general-navigation">
          <MiProgresoTituloComponent />
          <CardBienvenida
            cursoPersona={cursoPersona}
            Persona={persona}
            curso={curso}
            cursoExpirado={estadoExpiracion.expirado}
            porcentajeCursoAvance={porcentajeCursoAvance}
            cursoExpiradoAmpliacion={estadoExpiracion.ampliacion}
            cursoExpiradoMembresia={estadoExpiracion.membresia}
            Evaluaciones={evaluaciones}
            listaUnidades={listaUnidades}
            spinnerMateriales={spinnerMateriales}
            isButtonDisabledMateriales={isButtonDisabledMateriales}
            setContenidoAlertActivar={setContenidoAlertActivar}
            setShowAlert={setShowAlert}
            setspinnerMateriales={setSpinnerMateriales}
            setButtonDisabledMateriales={setButtonDisabledMateriales}
            setspinnerCertificado={setSpinnerCertificado}
            spinnerCertificado={spinnerCertificado}
            setspinnerNotas={setSpinnerNotas}
            spinnerNotas={spinnerNotas}
            unidadesProbadasNotaMinima={unidadesProbadasNotaMinima}
          />

          <GroupCardDownload
            cursoPersona={cursoPersona}
            isButtonDisabledMateriales={isButtonDisabledMateriales}
            spinnerMateriales={spinnerMateriales}
            setButtonDisabledMateriales={setButtonDisabledMateriales}
            setspinnerMateriales={setSpinnerMateriales}
            curso={curso}
            Evaluaciones={evaluaciones}
            listaUnidades={listaUnidades}
            duracionEnHoras={duracionEnHoras}
            Persona={persona}
            setspinnerCertificado={setSpinnerCertificado}
            spinnerCertificado={spinnerCertificado}
            setspinnerNotas={setSpinnerNotas}
            spinnerNotas={spinnerNotas}
          />

          {/* Tabs de Unidades y Progreso */}
          <div className="p-fluid formgrid grid">
            <TabUnidadesComponent
              listaUnidades={listaUnidades}
              listaLecciones={listaLecciones}
              cursoPersona={cursoPersona}
            />

            {/* Desktop */}
            <div className="field col-12 md:col-5 progreso-desktop-block">
              <div className="border rounded-9 shadow pt-3 pl-5 pr-2 pb-3 p-md-4 mb-md-4 cardTabs card3Y4min-height">
                <span className="subtitleAvanceUnidades h4">Avance de unidades</span>
                
                <div style={{ marginLeft: "10px" }}>
                  {listaUnidades.map((unidad, index) => {
                    const progresoActual = progresoUnidad.find(p => p.idUnidad === unidad.idUnidad);
                    const porcentaje = progresoActual?.promedioPorcentaje || 0;
                    const pendientes = leccionesPendientes?.find(l => l.idUnidad === unidad.idUnidad);
                    
                    const tooltipContent = porcentaje > 0 && porcentaje < 100 && pendientes?.leccionesPendientes?.length
                      ? `¡Termina tus videos! Aún no has culminado la(s) lección(es): ${pendientes.leccionesPendientes.join(",")}`
                      : "";
                    
                    return (
                      <div key={unidad.idUnidad} className="p-fluid formgrid grid">
                        <div className="field col-12 md:col-2" style={{ paddingLeft: 0, paddingRight: 0 }}>
                          <b className="fontsizesmall">Unidad {index + 1}</b>
                        </div>
                        
                        <div className="field col-12 md:col-5 bar-progress-unidades">
                          {tooltipContent && (
                            <Tooltip
                              target={`.ProgressBar${unidad.idUnidad}`}
                              content={tooltipContent}
                              position="top"
                              className="white-tooltip"
                            />
                          )}
                          <ProgressBar
                            className={`ProgressBar${unidad.idUnidad}`}
                            style={{ height: "6px", borderRadius: "0px" }}
                            value={porcentaje}
                          />
                        </div>
                        
                        <div className={`field col-12 md:col-1 ProgressBar${unidad.idUnidad}`}>
                          <span className="fontsizesmall" style={{ color: "blue" }}>
                            {porcentaje}%
                          </span>
                        </div>
                        
                        <div className="field col-12 md:col-4">
                          <UnidadEvaluacion idUnidad={unidad.idUnidad} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                
                <span className="subtitle h4">
                  <VistaPromedio promedio={cursoPersona[0].promedio || "0"} />
                </span>
              </div>
            </div>

            {/* Mobile */}
            <div className="field col-12 md:col-5 progreso-mobile movilBackground">
              <div className="rounded-9 shadow p-3 p-md-4 mb-4 mb-md-4 cardTabs">
                <span className="subtitleAvanceUnidades h4">Avance de unidades</span>
                
                {listaUnidades.map((unidad, index) => {
                  const progresoActual = progresoUnidad.find(p => p.idUnidad === unidad.idUnidad);
                  const porcentaje = progresoActual?.promedioPorcentaje || 0;
                  
                  return (
                    <div key={unidad.idUnidad}>
                      <div className="contenido-avance-unidad-mobile">
                        <div>
                          <b className="fontsizesmall">Unidad {index + 1}</b>
                        </div>
                        
                        <div style={{ marginTop: "7px" }}>
                          <ProgressBar
                            className={`ProgressBar${unidad.idUnidad}`}
                            style={{ height: "8px", borderRadius: "0px" }}
                            value={porcentaje}
                          />
                        </div>
                        
                        <div>
                          <span className="fontsizesmall" style={{ color: "blue" }}>
                            {porcentaje}%
                          </span>
                        </div>
                        
                        <div className="field col-12 md:col-3">
                          <UnidadEvaluacion idUnidad={unidad.idUnidad} className="w-100" />
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                <span className="subtitle h4">
                  <VistaPromedio promedio={cursoPersona[0].promedio || "0"} />
                </span>
              </div>
            </div>
          </div>

          {/* Biblioteca y FAQs */}
          <div style={{ marginRight: "8px" }}>
            <BibliotecaVirtualComponent
              listaBiblioteca={listaBiblioteca}
              cursoPersona={cursoPersona}
            />
          </div>
          
          <div style={{ marginRight: "8px" }}>
            <PreguntasFrecuentesComponent />
          </div>
        </div>

        <FooterInterno />

        {/* Diálogo de Activación */}
        <DialogZegel
          header=""
          visible={showAlert}
          width="30vw"
          onHide={() => setShowAlert(false)}
          footer={footerActivar}
          contenido={contenidoAlertActivar}
        />
      </div>
    </>
  );
};

export default ProgresoCurso;